import { useMemo, useState } from "react";
import type { AnyBlock, LineItem } from "@/types";
import { computeTotals, formatMoney, defaultSelectedOptionalIds } from "@/lib/money";
import { RendererCtx, useRenderer, type QuoteContext, type RendererMode } from "./context";
import { BackgroundWrapper } from "./BackgroundWrapper";
import {
  CoverBlock,
  RichTextBlock,
  ImageBlock,
  PricingBlock,
  TermsBlock,
  AcceptBlock,
  AuthorBlock,
  TestimonialBlock,
  SplitBlock,
  FeatureGridBlock,
  EmbedBlock,
  CdIntroBlock,
  CdDrawingsBlock,
  CdVrBlock,
  CdTermsBlock,
} from "./blocks";
import "./renderer.css";

export interface QuoteRendererProps {
  blocks: AnyBlock[];
  lineItems: LineItem[];
  mode: RendererMode;
  /** Quote-record context for cover ref/contact/site + pricing currency/VAT. */
  quote?: QuoteContext;
  /** Controlled optional-item selection (public). Omit in preview → local. */
  selectedOptionalIds?: number[];
  onToggleOptional?: (id: number) => void;
  /** Accept/decline handlers — supplied only on the public page. */
  onAccept?: (name?: string) => void;
  onDecline?: (reason?: string) => void;
  /** Accept widget UX state (public). */
  acceptBusy?: boolean;
  acceptError?: string;
}

/** Blocks that paint their own full-bleed section (background image + bespoke
 *  scrim) and therefore skip the standard band wrapper. */
const FULL_BLEED = new Set(["cd_vr"]);

/**
 * The single shared renderer (§7). The public page and the builder's preview
 * pane both render through this — identical markup; only `mode` forks
 * behaviour. Pure presentational: no data fetching, no network calls.
 *
 * CD layout: a full-bleed, banded page — each block is an edge-to-edge band
 * (colour from its background modifier) with an inner 1120px container, wrapped
 * by `qr-theme` which supplies the CD chrome (sticky header + footer).
 */
export function QuoteRenderer(props: QuoteRendererProps) {
  const {
    blocks,
    lineItems,
    mode,
    quote,
    selectedOptionalIds,
    onToggleOptional,
    onAccept,
    onDecline,
    acceptBusy,
    acceptError,
  } = props;

  // When selection isn't controlled (preview), keep it local for visual
  // fidelity — toggling never POSTs anything. Seed default-selected optionals
  // once on mount (spec §6); user toggles own the state thereafter.
  const [localSelected, setLocalSelected] = useState<number[]>(() =>
    defaultSelectedOptionalIds(lineItems),
  );
  const selected = selectedOptionalIds ?? localSelected;
  const toggle =
    onToggleOptional ??
    ((id: number) =>
      setLocalSelected((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
      ));

  const ctx = useMemo(
    () => ({
      mode,
      quote,
      lineItems,
      selectedOptionalIds: selected,
      onToggleOptional: toggle,
      onAccept,
      onDecline,
      acceptBusy,
      acceptError,
    }),
    [mode, quote, lineItems, selected, toggle, onAccept, onDecline, acceptBusy, acceptError],
  );

  return (
    <RendererCtx.Provider value={ctx}>
      <div className="qr-theme">
        <StickyHeader />
        <div className="block-doc">
          {blocks.map((block, i) => {
            // Only the hero uses this — it earns its scroll cue when there is
            // in fact something below it to scroll to.
            const hasBlocksBelow = i < blocks.length - 1;
            // data-section-key = block.type feeds the engagement beacon on the public page (the
            // builder preview carries the attributes too, harmlessly — no beacon runs there).
            // Blocks of the same type share a key and merge in the dwell chart; the canonical
            // key list lives in Xano api 335, which must grow with any new block type.
            return FULL_BLEED.has(block.type) ? (
              <BlockSwitch key={block.id} block={block} hasBlocksBelow={hasBlocksBelow} />
            ) : (
              <BackgroundWrapper
                key={block.id}
                background={block.background}
                blockType={block.type}
                sectionKey={block.type}
              >
                <BlockSwitch block={block} hasBlocksBelow={hasBlocksBelow} />
              </BackgroundWrapper>
            );
          })}
        </div>
        <footer className="qr-foot">
          <div className="qr-foot__inner">
            <img className="qr-foot__mark" src="/cd/wordmark-reverse.png" alt="Arkitectly" />
            <a href="mailto:hello@arkitectly.co.uk" className="qr-foot__contact">
              hello@arkitectly.co.uk
            </a>
          </div>
        </footer>
      </div>
    </RendererCtx.Provider>
  );
}

/** CD sticky bar: brandmark + live total (from the current selection) + an
 *  Accept-&-sign link that smooth-scrolls to the accept block. Hidden in print. */
function StickyHeader() {
  const { quote, lineItems, selectedOptionalIds } = useRenderer();
  const currency = quote?.currency ?? "GBP";
  const vatTreatment = quote?.vat_treatment || "inclusive";
  const totals = computeTotals(lineItems, vatTreatment, selectedOptionalIds);
  const hasItems = lineItems.length > 0;

  return (
    <header className="qr-bar">
      <div className="qr-bar__inner">
        <img className="qr-bar__mark" src="/cd/brandmark.png" alt="Arkitectly" />
        <div className="qr-bar__right">
          {hasItems ? (
            <div className="qr-bar__total">
              <span className="eyebrow qr-bar__eyebrow">Current total</span>
              <span className="num numeric qr-bar__amount">{formatMoney(totals.total, currency)}</span>
            </div>
          ) : null}
          <a href="#accept" className="qr-bar__cta">
            Accept &amp; sign
          </a>
        </div>
      </div>
    </header>
  );
}

function BlockSwitch({ block, hasBlocksBelow }: { block: AnyBlock; hasBlocksBelow?: boolean }) {
  switch (block.type) {
    case "cover":
      return (
        <CoverBlock
          block={block as Extract<AnyBlock, { type: "cover" }>}
          showScrollCue={hasBlocksBelow}
        />
      );
    case "rich_text":
      return <RichTextBlock block={block as Extract<AnyBlock, { type: "rich_text" }>} />;
    case "image":
      return <ImageBlock block={block as Extract<AnyBlock, { type: "image" }>} />;
    case "pricing":
      return <PricingBlock block={block as Extract<AnyBlock, { type: "pricing" }>} />;
    case "terms":
      return <TermsBlock block={block as Extract<AnyBlock, { type: "terms" }>} />;
    case "accept":
      return <AcceptBlock block={block as Extract<AnyBlock, { type: "accept" }>} />;
    case "author":
      return <AuthorBlock block={block as Extract<AnyBlock, { type: "author" }>} />;
    case "quote":
      return <TestimonialBlock block={block as Extract<AnyBlock, { type: "quote" }>} />;
    case "split":
      return <SplitBlock block={block as Extract<AnyBlock, { type: "split" }>} />;
    case "feature_grid":
      return <FeatureGridBlock block={block as Extract<AnyBlock, { type: "feature_grid" }>} />;
    case "embed":
      return <EmbedBlock block={block as Extract<AnyBlock, { type: "embed" }>} />;
    // Claude Design fixed-layout sections (forward-compatible block types)
    case "cd_intro":
      return <CdIntroBlock block={block} />;
    case "cd_drawings":
      return <CdDrawingsBlock block={block} />;
    case "cd_vr":
      return <CdVrBlock block={block} />;
    case "cd_terms":
      return <CdTermsBlock block={block} />;
    default:
      // Unknown / forward-compatible block type — render nothing, never throw.
      return null;
  }
}
